import { useMutation, useReadQuery, useSuspenseQuery } from '@apollo/client/react';
import { useTransition, useRef, useCallback, useMemo, startTransition, useState } from 'react';
import { debounce } from 'lodash';
import { Plus, UserRoundSearch } from 'lucide-react';
import { toast } from 'sonner';
import { createColumnHelper, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';

import { UsersDocument } from 'src/graphql/actions/user/users.query';
import { AppMembershipRoleEnum, QueryOrderEnum, UserColumnsEnum } from 'src/graphql/schema';
import { AppMembershipsCreateDocument } from 'src/graphql/actions/appMemberships/appMembershipsCreate.mutation';
import { ClientError, getApiError } from 'src/utils/errors';
import { withSuspense } from 'src/hoc';
import { InputSearch } from '@/components/ui/input-search';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { getGravatarUrl } from 'src/utils/getGravatarUrl';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableRow } from '@/components/ui/table';
import { EmptyBlock } from '@/components/ui/empty-block';
import { useInfiniteScroll } from 'src/hooks/use-infinite-scroll';
import { cn } from 'src/lib/utils';

import { refetchAppMemberships } from '../api';
import { useAppMembershipsManagement } from '../model';

import type { IUsersQuery } from 'src/graphql/actions/user/users.query';
import type { IAppMembershipsQuery } from 'src/graphql/actions/appMemberships/appMemberships.query';

import AddUserDialog from './AddUserDialog';

type IUserRow = {
  id: string;
  user: {
    name: string;
    email: string;
    isAdded: boolean;
  };
};

const AddMemberButton = ({ id: userId = '', canAdd = false }) => {
  const { appId } = useAppMembershipsManagement();
  const [createAppMemberships] = useMutation(AppMembershipsCreateDocument);
  const [isAdding, startAddTransition] = useTransition();

  const grantViewerAccess = () =>
    startAddTransition(async () => {
      try {
        const { data } = await createAppMemberships({
          variables: {
            input: {
              appId,
              appMembershipsParams: [{ userId, role: AppMembershipRoleEnum.Viewer }],
            },
          },
        });

        if (!data?.appMembershipsCreate?.success) throw new ClientError(data?.appMembershipsCreate?.errors?.[0]);

        toast.success('Member access has been granted');
        await refetchAppMemberships();
      } catch (error) {
        getApiError(error, toast.error);
      }
    });

  if (!canAdd) return <span className="font-semibold text-xs text-muted-foreground">Added</span>;

  return (
    <Button
      variant="ghost"
      size="sm"
      className="shrink-0"
      onClick={grantViewerAccess}
      disabled={isAdding}
      isLoading={isAdding}
    >
      Add
      <Plus />
    </Button>
  );
};

const columnHelper = createColumnHelper<IUserRow>();
const userColumn = columnHelper.accessor('user', {
  cell: (props) => {
    const { name, email } = props.getValue();
    const fallback = name?.charAt(0).toUpperCase();

    return (
      <div className="flex items-center gap-2">
        <Avatar className="size-6.5 rounded-md shrink-0">
          <AvatarImage src={getGravatarUrl(email)} />
          <AvatarFallback className="rounded-md bg-primary text-white">{fallback}</AvatarFallback>
        </Avatar>
        <div className="grow min-w-0 text-sm text-foreground truncate">{name}</div>
      </div>
    );
  },
});
const addMemberColumn = columnHelper.accessor('id', {
  cell: (props) => (
    <div className="flex justify-end">
      <AddMemberButton id={props.row.original.id} canAdd={!props.row.original.user.isAdded} />
    </div>
  ),
});

const USERS_PER_PAGE = 20;

const OrganizationMembers = () => {
  const [queryFilter, setQueryFilter] = useState<string | undefined>(undefined);
  const [isPending, startSearchTransition] = useTransition();
  const tableRef = useRef<HTMLDivElement>(null);
  const requestedFromRef = useRef<IUsersQuery['users']['nodes'] | null>(null);
  const { queryAppMembershipsRef, canCreateMember } = useAppMembershipsManagement();
  const {
    data: { users },
    refetch,
    fetchMore,
  } = useSuspenseQuery(UsersDocument, {
    variables: {
      page: 1,
      perPage: USERS_PER_PAGE,
      orderBy: UserColumnsEnum.CreatedAt,
      order: QueryOrderEnum.Desc,
      ...(queryFilter && { queryFilter }),
    },
  });
  const {
    data: { appMemberships },
  } = useReadQuery(queryAppMembershipsRef);

  const nextPage = Math.ceil(users.nodes.length / USERS_PER_PAGE) + 1;

  const handleLoadMore = () => {
    if (requestedFromRef.current === users.nodes) return Promise.resolve();

    requestedFromRef.current = users.nodes;

    return new Promise<void>((resolve) => {
      startTransition(async () => {
        try {
          await fetchMore({
            variables: {
              page: nextPage,
              perPage: USERS_PER_PAGE,
              orderBy: UserColumnsEnum.CreatedAt,
              order: QueryOrderEnum.Desc,
              ...(queryFilter && { queryFilter }),
            },
            updateQuery: (prev, { fetchMoreResult }) => {
              if (!fetchMoreResult?.users?.nodes?.length) return prev;

              const loadedIds = new Set(prev.users.nodes.map(({ id }) => id));

              return {
                ...prev,
                users: {
                  ...fetchMoreResult.users,
                  nodes: [...prev.users.nodes, ...fetchMoreResult.users.nodes.filter(({ id }) => !loadedIds.has(id))],
                },
              };
            },
          });
        } catch {
          requestedFromRef.current = null;
          toast.error('Failed to load more users');
        } finally {
          resolve();
        }
      });
    });
  };

  const { sentinelRef } = useInfiniteScroll({
    rootRef: tableRef,
    hasNextPage: users.hasNextPage && !isPending,
    onLoadMore: handleLoadMore,
  });

  const debouncedOnSearch = useRef(
    debounce((e: React.ChangeEvent<HTMLInputElement>) => {
      const searchValue = e.target.value;
      startSearchTransition(() => setQueryFilter(searchValue));
    }, 500),
  ).current;

  const handleClear = () => {
    debouncedOnSearch.cancel();
    startSearchTransition(() => setQueryFilter(undefined));
  };

  const onClear = useCallback(handleClear, [refetch]);

  const userRows = useMemo(() => toUserRows(users.nodes, appMemberships.nodes), [users.nodes, appMemberships.nodes]);

  const columns = useMemo(() => (canCreateMember ? [userColumn, addMemberColumn] : [userColumn]), [canCreateMember]);

  const table = useReactTable({
    data: userRows,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="space-y-2.5 overflow-hidden flex flex-col py-3.5 px-4">
      <h2 className="text-sm text-foreground">Organization members</h2>
      <InputSearch
        placeholder="Search name or email"
        onChange={debouncedOnSearch}
        onClear={onClear}
        isLoading={isPending}
      />
      <div className="rounded-md border border-border overflow-hidden">
        <Table ref={tableRef} maxHeight="max-h-[280px]" className="table-fixed">
          <colgroup>
            <col />
            {canCreateMember && <col className="w-25" />}
          </colgroup>
          <TableBody>
            {userRows.length ? (
              table.getRowModel().rows.map((row, index) => (
                <TableRow
                  key={row.id}
                  className={cn('hover:bg-white', { 'border-0': table.getRowModel().rows.length - 1 === index })}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} className="py-2.25">
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="p-6">
                  <EmptyBlock
                    withCard={false}
                    icon={UserRoundSearch}
                    title={queryFilter ? 'No results found' : 'You don’t have members in your organization'}
                    action={<AddUserDialog onSuccess={handleClear} />}
                    classes={{
                      title: 'text-sm',
                    }}
                  />
                </TableCell>
              </TableRow>
            )}

            <TableRow className="border-0 hover:bg-white">
              <TableCell colSpan={columns.length} className="p-0">
                <div ref={sentinelRef} aria-hidden="true" />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default withSuspense(OrganizationMembers);

function toUserRows(
  users: IUsersQuery['users']['nodes'],
  memberships: IAppMembershipsQuery['appMemberships']['nodes'],
) {
  const memberIds = new Set(memberships.map(({ user: { id } }) => id));

  return users.map((user) => ({
    id: user.id,
    user: {
      name: user.name || '',
      email: user.email,
      isAdded: memberIds.has(user.id),
    },
  }));
}
